<template>
  <v-container fluid class="enroll-page">
    <!-- Alert Banner -->
    <AAlertBanner :messages="alertError" title="Warning" :icon-size="22" />

    <!-- Confirmation Dialogs -->
    <TheModalConfirmation
      ref="taxpodAccEnrollConfirmPopup"
      @agree="modalTaxpodAccEnroll()"
    ></TheModalConfirmation>

    <TheModalConfirmation
      ref="rejectConfirmPopup"
      @agree="modalRefundEmailReject()"
    ></TheModalConfirmation>

    <!-- Header -->
    <div class="enroll-header">
      <div class="enroll-header__top">
        <div class="enroll-header__info" v-if="!loadingDialog">
          <h1 class="enroll-header__title">{{ model.add.name }}</h1>
        </div>
        <div class="enroll-header__actions" v-if="isTaxpodAdminRoles && alertError.length == 0">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--red"
                v-bind="attrs"
                v-on="on"
                @click="toggleRejectDialog()"
              >
                <v-icon size="18">mdi-file-remove</v-icon>
              </button>
            </template>
            <span>Reject Refund Email</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--emerald"
                v-bind="attrs"
                v-on="on"
                @click="toggleSubmitDialog()"
              >
                <v-icon size="18">mdi-check-circle-outline</v-icon>
              </button>
            </template>
            <span>Submit</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Enrollment Date Section -->
    <div class="section-card">
      <div class="section-header">
        <v-icon color="white" small>mdi-calendar-check-outline</v-icon>
        <span class="section-header__title">Enrollment Date</span>
      </div>
      <div class="section-body">
        <div class="form-row">
          <label class="form-row__label">Enrollment Date</label>
          <div class="form-row__input">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="rv-datepicker__trigger"
                  v-bind="attrs"
                  v-on="on"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                  </svg>
                  <span v-if="formData.enrollmentDate" class="rv-datepicker__text">{{ formData.enrollmentDate }}</span>
                  <span v-else class="rv-datepicker__placeholder">Select date...</span>
                </div>
              </template>
              <v-date-picker
                v-model="formData.enrollmentDate"
                no-title
                scrollable
                color="var(--app-accent)"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
                <v-btn text color="primary" @click="$refs.menu.save(date)">OK</v-btn>
              </v-date-picker>
            </v-menu>
          </div>
        </div>
      </div>
    </div>

    <div class="cards-grid">
    <!-- Application Info Section -->
    <div class="section-card">
      <div class="section-header">
        <v-icon color="white" small>mdi-office-building-outline</v-icon>
        <span class="section-header__title">Application Info</span>
      </div>
      <div class="section-body">
        <div class="detail-row" @click="copyContent(formData.companyName)">
          <span class="detail-label">Company Name</span>
          <span class="detail-val">{{ formData.companyName }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>
        <div class="detail-row" @click="copyContent(formData.salesperson)">
          <span class="detail-label">Salesperson</span>
          <span class="detail-val">{{ formData.salesperson }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>
        <div class="detail-row" @click="copyContent(formData.price)">
          <span class="detail-label">Total Price</span>
          <span class="detail-val">{{ formData.price }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>
        <div class="detail-row" @click="copyContent(formData.taxpodType)">
          <span class="detail-label">taxPOD Type</span>
          <span class="detail-val">{{ formData.taxpodType }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>
        <div class="detail-row" @click="copyContent(formData.hrdcInvoiceNo)">
          <span class="detail-label">HRDC Invoice No</span>
          <span class="detail-val">{{ formData.hrdcInvoiceNo }}
            <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- HRDC Invoice Attachment -->
        <div class="detail-row" v-if="formData.hrdcInvoiceFileUrl">
          <span class="detail-label">HRDC Invoice</span>
          <div class="detail-atch-actions">
            <button
              class="atch-btn atch-btn--view"
              @click="previewOrDownloadAttachment(formData.hrdcInvoiceFileUrl, 'preview')"
            >
              <v-icon size="16" class="mr-1">mdi-eye-outline</v-icon>
              View
            </button>
            <button
              class="atch-btn atch-btn--download"
              @click="previewOrDownloadAttachment(formData.hrdcInvoiceFileUrl, 'download')"
            >
              <v-icon size="16" class="mr-1">mdi-download</v-icon>
              Download
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Enrollment Details Section -->
    <div class="section-card">
      <div class="section-header">
        <v-icon color="white" small>mdi-account-details-outline</v-icon>
        <span class="section-header__title">Enrollment Details</span>
      </div>
      <div class="section-body">
        <!-- Primary Enrollment -->
        <div class="enrollment-card">
          <div class="enrollment-card__badge">Primary</div>
          <div class="detail-row" @click="copyContent(formData.firstName)">
            <span class="detail-label">First Name</span>
            <span class="detail-val">{{ formData.firstName }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(formData.lastName)">
            <span class="detail-label">Last Name</span>
            <span class="detail-val">{{ formData.lastName }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(formData.contactNo)">
            <span class="detail-label">Contact No</span>
            <span class="detail-val">{{ formData.contactNo }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(formData.email)">
            <span class="detail-label">Email</span>
            <span class="detail-val">{{ formData.email }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
        </div>

        <!-- Dynamic Enrollments -->
        <div
          v-for="(enrollment, index) in formData.dynamicEnrollmentDetails"
          :key="index"
          class="enrollment-card"
        >
          <div class="enrollment-card__badge enrollment-card__badge--secondary">Additional {{ index + 1 }}</div>
          <div class="detail-row" @click="copyContent(enrollment.firstNameAccEnrollment)">
            <span class="detail-label">First Name</span>
            <span class="detail-val">{{ enrollment.firstNameAccEnrollment }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(enrollment.lastNameAccEnrollment)">
            <span class="detail-label">Last Name</span>
            <span class="detail-val">{{ enrollment.lastNameAccEnrollment }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(enrollment.contactNoAccEnrollment)">
            <span class="detail-label">Contact No</span>
            <span class="detail-val">{{ enrollment.contactNoAccEnrollment }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
          <div class="detail-row" @click="copyContent(enrollment.emailAccEnrollment)">
            <span class="detail-label">Email</span>
            <span class="detail-val">{{ enrollment.emailAccEnrollment }}
              <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
            </span>
          </div>
        </div>
      </div>
    </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";

export default {
  mixins: [hrdcFunction],
  components: {
    TheModalConfirmation,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationDetails: new Api(),
    apiDownloadOrPreviewAttachment: new Api(),
    apiSubmit: new Api(),
    apiReject: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
      firstName: null,
      lastName: null,
      contactNo: null,
      email: null,
      companyName: null,
      salesperson: null,
      taxpodType: null,
      hrdcInvoiceNo: null,
      hrdcInvoiceFileUrl: null,
      price: null,
      enrollmentDate: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      dynamicEnrollmentDetails: null,
    },
    errorField: {
      applicationNo: null,
      firstName: null,
      lastName: null,
      contactNo: null,
      email: null,
      enrollmentDate: null,
    },
    loadingDialog: false,
    taxpodAccEnrollDialog: false,
    editMode: false,
    refundEmailRejectDialog: false,
  }),
  async created() {
    this.loadingDialog = !this.loadingDialog;

    const fieldMap = {
      applicationNo: "applicationUuid",
      firstName: "firstNameAccEnrollment",
      lastName: "lastNameAccEnrollment",
      contactNo: "contactNoAccEnrollment",
      email: "emailAccEnrollment",
    };
    for (let field in fieldMap) {
      this.formData[field] = this.$store.state.hrdc.data[fieldMap[field]];
    }

    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetApplicationDetails: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiDownloadOrPreviewAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/azure-download-or-preview-application-attachment`,
      },
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/enrollment_detail/create-enrollment-detail`,
        method: "post",
      },

      apiReject: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/reject-refund-email`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.apiGetApplicationDetails.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (status) {
          let taxpodType = null;
          let stageToGetDate = null;
          if (data.taxpodType == 1) {
            taxpodType = "New Sale";
            stageToGetDate =
              this.hrdcData.stagePriority.taxpodAccountActivation;
          } else {
            taxpodType = "Renewal";
            stageToGetDate = this.hrdcData.stagePriority.hrdcClaimCompleted;
          }

          this.formData.salesperson = data.referByName;
          this.formData.companyName = data.oriCompanyName;
          this.formData.price = `RM ${this.formatNumber(data.totalPrice)}`;
          this.formData.taxpodType = taxpodType;
          this.formData.hrdcInvoiceNo = data.manualInvoiceToHrdc ?? "-";

          // Check whether got hrdc invoice or not.
          let hrdcInvoiceFileUrl = data.ApplicationAttachment.find(
            (item) =>
              item.stageId == this.hrdcData.stagePriority.manualInvoiceToHrdc
          );
          if (hrdcInvoiceFileUrl) {
            hrdcInvoiceFileUrl = hrdcInvoiceFileUrl.fileUrl;
            this.formData.hrdcInvoiceFileUrl = hrdcInvoiceFileUrl;
          }

          // Assign dynamic enrollment details.
          if (data.ApplicationEnrollmentDetail.length > 0) {
            this.formData.dynamicEnrollmentDetails =
              data.ApplicationEnrollmentDetail;
          }

          // If editMode, then append enrollment date.
          if (this.editMode) {
            const enrollmentDate = data.ApplicationLog.find(
              (item) => item.stageId == stageToGetDate
            ).startDate;
            this.formData.enrollmentDate =
              moment(enrollmentDate).format("YYYY-MM-DD");
          }
        }

        this.loadingDialog = !this.loadingDialog;
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationDetails.fetch();

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state.hrdc.data.stageId];
    this.rolesAssignation(this.auth.roles);
    if (!this.isTaxpodAdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
    }

    if (currentStageId >= this.hrdcData.stageSlug.taxpodAccountActivation) {
      this.alertError.push("You already enrolled taxPOD account");
      this.editMode = true;
    }

    if (currentStageId < this.hrdcData.stageSlug.paymentReceivedFromHrdc) {
      this.alertError.push("Please complete previous step");
    }
  },
  mounted() {},
  methods: {
    formatNumber(number) {
      if (number) {
        return Number(number).toLocaleString();
      }

      return number;
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    toggleSubmitDialog() {
      this.$refs.taxpodAccEnrollConfirmPopup.open({
        title: "taxPOD Account Enrollment",
        message: "Are you sure to submit?",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Submit",
        },
        agree: () => {
          this.modalTaxpodAccEnroll();
        },
      });
    },
    toggleRejectDialog() {
      this.$refs.rejectConfirmPopup.open({
        title: "Reject Application",
        message: "Please confirm that you wish to reject this application. Application will flow back to pending payment.",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Reject",
        },
        agree: () => {
          this.modalRefundEmailReject();
        },
      });
    },
    modalTaxpodAccEnroll() {
      this.taxpodAccEnrollDialog = false;
      this.submit();
    },
    copyContent(item) {
      const textarea = document.createElement("textarea");
      textarea.value = item;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");

      // Remove the temporary textarea
      document.body.removeChild(textarea);

      this.$store.dispatch("showMessage", {
        message: "Copied Successfully!",
        messageType: "info",
      });
    },
    redirectBack() {
      let route = "HrdcApplication";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state.hrdc.data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    isImage(url) {
      return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
    },
    async previewOrDownloadAttachment(fileUrl, action) {
      this.loadingDialog = !this.loadingDialog;

      const attachmentExt = fileUrl.split(".")[1].toLowerCase();

      fileUrl = fileUrl.replace(/\.[^.]+$/, (match) => match.toLowerCase());

      let responseType = "blob";
      let isImage = false;
      let blobType = `application/${attachmentExt}`;
      let streamUrl = this.apiDownloadOrPreviewAttachment.url;
      let downloadAttachmentName = `${this.formData.companyName} - ${this.formData.hrdcInvoiceNo}.${attachmentExt}`;

      if (this.isImage(fileUrl)) {
        responseType = "arraybuffer";
        isImage = true;
        blobType = `image/${attachmentExt.toLowerCase()}`;
      }

      await this.$axios
        .get(streamUrl, {
          responseType,
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: fileUrl,
            isImage,
          },
        })
        .then((response) => {
          if (action == "preview") {
            const blobUrl = URL.createObjectURL(
              new Blob([response.data], { type: blobType })
            );

            window.open(blobUrl, "_blank");
          } else if (action == "download") {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadAttachmentName);
            document.body.appendChild(link);
            link.click();
          }
        })
        .catch((error) => {
          if ((error.response.status == 404) | true) {
            this.$store.dispatch("showMessage", {
              message:
                "Failed to Preview/Download attachment, kindly contact administrator",
              messageType: "error",
              timeout: 2000,
            });
          }
        });

      this.loadingDialog = !this.loadingDialog;
    },
    async submit() {
      this.loadingDialog = !this.loadingDialog;
      let data = {
        applicationUuid: this.formData.applicationNo,
        enrollmentDate: this.formData.enrollmentDate,
        enrollById: this.auth.uuid,
      };

      this.apiSubmit.setParams(data);
      this.apiSubmit.fetch();
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialog = !this.loadingDialog;
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.applicationNotFound) {
              this.$store.dispatch("showMessage", {
                message: response.data.applicationNo,
                messageType: "error",
                timeout: 2000,
              });
            }
          }

          if (response.status) {
            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
                tabIndex: 0,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
    },
    modalRefundEmailReject() {
      this.loadingDialog = !this.loadingDialog;

      this.apiReject.setParams({
        applicationUuid: this.$route.query._ausk,
        userUuid: this.auth.uuid,
        rejectToStage: this.hrdcData.stagePriority.claimApprovedFromHrdc,
        rejectStageId:
          this.hrdcData.stagePriority.paymentReceivedFromHrdcRejected,
      });

      this.apiReject.setCallbackCompleted((response) => {
        try {
          this.loadingDialog = !this.loadingDialog;
          if (response.status) {
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
              },
            });
          }
        } catch (err) {
          this.loadingDialog = !this.loadingDialog;
          console.log(err);
        }
      });

      this.apiReject.fetch();
      this.refundEmailRejectDialog = false;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .enroll-page {
  background-color: var(--app-page);
}

.dark-mode .enroll-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn--red {
  background: var(--app-danger-bg);
}

.dark-mode .hero-action-btn--emerald {
  background: var(--app-success-bg);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .detail-val {
  border: 1px solid var(--app-border);
}

.dark-mode .atch-btn--view {
  background: var(--app-danger-bg);
}

.dark-mode .atch-btn--download {
  background: var(--app-accent-soft);
}

.dark-mode .enrollment-card__badge {
  background: var(--app-accent-soft);
}

.dark-mode .enrollment-card__badge--secondary {
  background: var(--app-purple-bg);
}

.dark-mode .rv-datepicker__trigger {
  border: 1px solid var(--app-field-border);
  background: var(--app-field);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-field-border-hover);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.enroll-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.enroll-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.enroll-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.enroll-header__info {
  flex: 1;
  min-width: 0;
}

.enroll-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.enroll-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.hero-action-btn--red {
  background: #fee2e2;
  color: #dc2626;
}

.hero-action-btn--red:hover {
  background: #fecaca;
  color: #b91c1c;
}

.hero-action-btn--emerald {
  background: #dcfce7;
  color: #16a34a;
}

.hero-action-btn--emerald:hover {
  background: #a7f3d0;
  color: #065f46;
}

/* ============================================
   CARDS GRID
   ============================================ */
.cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  margin-bottom: 24px;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
}

/* ============================================
   DETAIL ROWS
   ============================================ */
.detail-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  cursor: pointer;
}

.detail-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 1px;
}

.detail-val {
  flex: 1;
  font-size: 0.85rem;
  color: #1a1e24;
  line-height: 1.5;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  background: #f1f3f5;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 7px 14px;
  transition: all 0.15s ease;
  cursor: pointer;
}

.detail-val:hover {
  background: #eef1f5;
  border-color: #c9cdd3;
}

/* Copy icon */
.copy-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 8px;
  color: #a0aab4 !important;
}

.detail-row:hover .copy-icon {
  opacity: 1;
}

/* ============================================
   ATTACHMENT BUTTONS
   ============================================ */
.detail-atch-actions {
  display: flex;
  gap: 8px;
}

.atch-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
}

.atch-btn--view {
  background: #fee2e2;
  color: #dc2626;
}

.atch-btn--view:hover {
  background: #fecaca;
}

.atch-btn--download {
  background: #dbeafe;
  color: #1d4ed8;
}

.atch-btn--download:hover {
  background: #bfdbfe;
}

/* ============================================
   ENROLLMENT CARDS
   ============================================ */
.enrollment-card {
  padding: 16px 0;
  border-bottom: 1px solid #eceef1;
}

.enrollment-card:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.enrollment-card:first-child {
  padding-top: 0;
}

.enrollment-card__badge {
  display: inline-block;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.enrollment-card__badge--secondary {
  background: #f5f3ff;
  color: #7c3aed;
}

/* ============================================
   FORM ROW (Datepicker)
   ============================================ */
.form-row {
  display: flex;
  align-items: center;
}

.form-row__label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #2d3239;
  line-height: 1.6;
  padding-right: 16px;
}

.form-row__input {
  flex: 1;
  max-width: 300px;
}

/* Datepicker trigger */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
}

.rv-datepicker__trigger:hover {
  border-color: #94a3b8;
}

.rv-datepicker__icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.rv-datepicker__text {
  color: #1e293b;
  font-weight: 600;
}

.rv-datepicker__placeholder {
  color: #94a3b8;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .enroll-header__top {
    padding: 16px 20px;
  }

  .enroll-header__actions {
    flex-direction: column;
    gap: 6px;
  }

  .section-body {
    padding: 16px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-label {
    flex: none;
  }

  .detail-val {
    width: 100%;
  }

  .form-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .form-row__label {
    flex: none;
    padding-right: 0;
  }

  .form-row__input {
    width: 100%;
    max-width: none;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .enroll-page { background-color: var(--app-page); }
.dark-mode .enroll-header { background: var(--app-card); }
.dark-mode .enroll-header__title { color: var(--app-text); }
.dark-mode .back-btn { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .back-btn:hover { background: var(--app-form-tile-hover); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .detail-label { color: var(--app-text-muted); }
.dark-mode .detail-val { background: var(--app-form-tile); border-color: var(--app-form-tile-border); color: var(--app-text); }
.dark-mode .detail-val:hover { background: var(--app-border); border-color: var(--app-border-strong); }
.dark-mode .enrollment-card { border-color: var(--app-border); }
.dark-mode .enrollment-card__badge { background: var(--app-accent-soft); color: var(--app-accent); }
.dark-mode .enrollment-card__badge--secondary { background: var(--app-purple-bg); color: var(--app-purple-fg); }
.dark-mode .form-row__label { color: var(--app-text-muted); }
.dark-mode .rv-datepicker__trigger { background: var(--app-field); border-color: var(--app-field-border); }
.dark-mode .rv-datepicker__text { color: var(--app-text); }
.dark-mode .atch-btn--view { background: var(--app-danger); color: var(--app-danger-fg); }
.dark-mode .atch-btn--download { background: var(--app-accent-soft); color: var(--app-accent-strong); }

</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-header .v-btn:hover {
  background: var(--app-form-tile-hover) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-date-picker-table .v-btn--disabled {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-form-tile) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* Date picker popup (unscoped — renders outside component) */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: 'Lato', sans-serif !important;
}

.v-picker .v-picker__body {
  border: none !important;
}

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

.v-picker__body {
  background: #fff !important;
}

.v-date-picker-header {
  padding: 12px 16px 8px !important;
  background: #fff !important;
}

.v-date-picker-header__value button {
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #1e293b !important;
}

.v-date-picker-header .v-btn {
  color: #64748b !important;
}

.v-date-picker-header .v-btn:hover {
  background: #f1f5f9 !important;
}

.v-date-picker-table thead th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
  padding: 8px 0 !important;
}

.v-date-picker-table .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}

.v-date-picker-table .v-btn:hover {
  background: #f0f9ff !important;
}

.v-date-picker-table .v-btn--active {
  background: #0ea5e9 !important;
  color: #fff !important;
  font-weight: 700 !important;
}

.v-date-picker-table .v-btn--active:hover {
  background: #0284c7 !important;
}

.v-date-picker-table .v-btn--outlined {
  border-color: #0ea5e9 !important;
  color: #0ea5e9 !important;
}

.v-date-picker-table .v-btn--disabled {
  color: #cbd5e1 !important;
}

.v-picker__actions {
  padding: 8px 16px 14px !important;
  background: #fff !important;
  border-top: 1px solid #f1f5f9 !important;
}

.v-picker__actions .v-btn {
  font-family: 'Lato', sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.82rem !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
}

.v-date-picker-years li,
.v-date-picker-months .v-btn {
  font-family: 'Lato', sans-serif !important;
}
</style>
